import { useCallback, useEffect } from "react"
import { useRouter } from "next/router"

import { Button, Loader, Tooltip } from "@coval/ui"
import { AlertTriangle } from "lucide-react"

import type { TestRecentSqlMetricResponse } from "@/types_api/backend/transport/test_recent_sql_metric_response"

import { SqlSimulationPreview } from "../routes/SqlSimulationPreview"
import { useTestSqlMetricTransport } from "../routes/useTestSqlMetricTransport"
import { formatNullableMetricValue } from "../utils/sqlMetricValue"

interface SqlTestAreaProps {
  testSimulationOutputId?: string
  selectedSimulationId?: string | null
  onSimulationChange?: (simulationOutputId: string | null) => void
  testRequestId?: number | null
  sqlQuery: string
  metricDisplayName: string
  aggregationMethod?: string
  unit?: string
  onTest?: () => void
  canTest?: boolean
}

export function SqlTestArea(props: SqlTestAreaProps) {
  const router = useRouter()
  return <ScopedSqlTestArea key={router.asPath.split("?")[0]} {...props} />
}

function ScopedSqlTestArea({
  sqlQuery,
  testRequestId,
  testSimulationOutputId,
  selectedSimulationId,
  onSimulationChange,
  metricDisplayName,
  aggregationMethod,
  unit,
  onTest,
  canTest,
}: SqlTestAreaProps) {
  const { mutate, reset, data, error, isPending } = useTestSqlMetricTransport()

  const runTest = useCallback(() => {
    mutate({
      sql_query: sqlQuery,
      aggregation_method: aggregationMethod,
      unit,
      ...(testSimulationOutputId
        ? { simulation_output_id: testSimulationOutputId }
        : {}),
    })
  }, [mutate, sqlQuery, aggregationMethod, unit, testSimulationOutputId])

  useEffect(() => {
    if (testRequestId === null) {
      reset()
      return
    }
    runTest()
    return () => {
      reset()
    }
  }, [runTest, reset, testRequestId])

  const isActiveTest =
    testRequestId !== null &&
    (!testSimulationOutputId ||
      !selectedSimulationId ||
      testSimulationOutputId === selectedSimulationId)
  const activeData = isActiveTest ? data : undefined
  const isTesting = isActiveTest && isPending && !error
  const legacyResult =
    activeData &&
    !activeData.simulation_results?.length &&
    !activeData.requires_simulations
      ? activeData
      : undefined
  return (
    <section aria-label="SQL metric test area">
      <SqlSimulationPreview
        results={activeData?.simulation_results}
        preferredSimulationId={selectedSimulationId}
        onSimulationChange={onSimulationChange}
        onTest={onTest}
        canTest={canTest}
        isTesting={isTesting}
        renderResult={(result) => (
          <SimulationMetricPreview
            result={result ?? (!isTesting ? legacyResult : undefined)}
            metricDisplayName={metricDisplayName}
            isTesting={isTesting}
            errorMessage={
              isActiveTest && error ? "Couldn't test this metric." : undefined
            }
            unavailableMessage={
              activeData?.requires_simulations && !isTesting
                ? activeData.error ||
                  "Run a simulation in this workspace, then test the query again."
                : undefined
            }
            isUnidentified={!result && Boolean(legacyResult)}
            onRetry={runTest}
          />
        )}
      />
    </section>
  )
}

type SqlMetricEvaluationResult = Pick<
  TestRecentSqlMetricResponse,
  "value" | "unit" | "error"
>

function SimulationMetricPreview({
  result,
  metricDisplayName,
  isTesting,
  errorMessage,
  unavailableMessage,
  isUnidentified,
  onRetry,
}: {
  result?: SqlMetricEvaluationResult
  metricDisplayName: string
  isTesting: boolean
  errorMessage?: string
  unavailableMessage?: string
  isUnidentified: boolean
  onRetry: () => void
}) {
  const failure =
    errorMessage ||
    (!isTesting && !unavailableMessage ? result?.error : undefined)
  const value =
    !failure && !isTesting && !unavailableMessage && result?.value != null
      ? formatNullableMetricValue(result.value, result.unit)
      : undefined
  const description = failure
    ? `${failure} Select to retry.`
    : isTesting
      ? "Testing metric…"
      : unavailableMessage ||
        (value !== undefined
          ? `${isUnidentified ? "Latest test result. This response does not identify its conversation. " : ""}${value}`
          : result
            ? "No value returned."
            : "Run Test Metric to calculate a value.")
  return (
    <Tooltip content={description}>
      <span
        role={failure ? "alert" : "status"}
        tabIndex={failure ? undefined : 0}
        aria-label={`Metric result for ${metricDisplayName}`}
        aria-description={description}
        aria-busy={isTesting || undefined}
        className={`inline-flex h-control w-28 shrink-0 items-center justify-center rounded-md border text-sm font-semibold tabular-nums ${
          failure
            ? "border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface"
            : value !== undefined
              ? "border-coval-success-border bg-coval-success-surface px-2 text-coval-success-on-surface"
              : unavailableMessage
                ? "border-coval-warning-border bg-coval-warning-surface px-2 text-coval-warning-on-surface"
                : "border-dotted border-coval-border px-2 text-coval-text-muted"
        }`}
      >
        {failure ? (
          <Button
            type="button"
            variant="ghost"
            className="h-full w-full text-current hover:text-current"
            aria-label="Retry metric test"
            onClick={onRetry}
          >
            <AlertTriangle className="shrink-0" />
            Error
          </Button>
        ) : isTesting ? (
          <Loader size="sm" label="Testing metric" />
        ) : unavailableMessage ? (
          <span className="truncate">Unavailable</span>
        ) : value !== undefined ? (
          <span className="truncate">{value}</span>
        ) : (
          <span className="sr-only">
            {result ? "No value returned" : "Not tested"}
          </span>
        )}
      </span>
    </Tooltip>
  )
}
